import { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  InfoHint,
  Input,
  SearchInput,
  Select,
  Table,
  TCell,
  THead,
  TRow,
} from '@cou/admin-ui';
import { JurisdictionBadge, SegmentPill } from '../players/badges';
import { playerDetails } from '../players/player-details';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import {
  defaultPlayersClient,
  type AdminPlayer,
  type PlayersClient,
  type SegmentOption,
} from '../players/players-client';
import { formatDateParts } from '../contests/format-date';

/**
 * The Players list (API-21, admin-ui.md → Players): every game user making Entries, searchable by
 * account ref. This is the support entry point — Caesars support supplies the ref, ops pastes it
 * here — so search is the screen's primary control rather than an afterthought.
 *
 * The rows are pseudonymous by construction (decision 7): the `users` table holds no name or
 * email, so there is nothing to redact and `users:view` is the only gate.
 */

export interface PlayersScreenProps {
  /** Injected for tests; defaults to the real API-21 client. */
  readonly client?: PlayersClient;
}

/** How long to let an operator keep typing before asking the server (they paste a full ref). */
const SEARCH_DEBOUNCE_MS = 250;

/** The Segment select's "no filter" sentinel — an operator-facing option, not a real ref. */
const ALL_SEGMENTS = 'all';

/** The unfiltered miss, and the message before any answer has landed (nothing renders then). */
const NO_PLAYERS_AT_ALL = 'No players yet.';

export function PlayersScreen({ client = defaultPlayersClient }: PlayersScreenProps) {
  const { getToken } = useAdminAuth();
  const [search, setSearch] = useState('');
  const [state, setState] = useState('');
  const [segment, setSegment] = useState(ALL_SEGMENTS);
  const [segments, setSegments] = useState<readonly SegmentOption[]>([]);
  const [players, setPlayers] = useState<AdminPlayer[] | null>(null);
  // Pinned to the answer on screen, not to the live controls: the operator can move a filter
  // while the next query is still out, and until it lands the rows are still the old query's.
  const [emptyMessage, setEmptyMessage] = useState(NO_PLAYERS_AT_ALL);
  const [error, setError] = useState<string | null>(null);

  // The Segment options load once — they are the registry, not a function of the current page,
  // so they don't move as the operator narrows the list.
  useEffect(() => {
    const token = getToken();
    if (token === null) return;
    let cancelled = false;
    void (async () => {
      const result = await client.listSegments(token);
      if (!cancelled && result.ok) setSegments(result.segments);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, getToken]);

  const load = useCallback(
    async (searchQuery: string, stateQuery: string) => {
      const token = getToken();
      if (token === null) return;
      const result = await client.listPlayers(token, {
        ...(searchQuery ? { search: searchQuery } : {}),
        ...(stateQuery ? { state: stateQuery } : {}),
        ...(segment === ALL_SEGMENTS ? {} : { segment }),
      });
      if (result.ok) {
        setPlayers(result.users);
        setEmptyMessage(emptyStateMessage({ search: searchQuery, state: stateQuery, segment }));
        setError(null);
      } else {
        // Never fall through to the empty state: "no such player" and "the lookup broke" are
        // opposite answers to give support, and only one of them is true.
        setError(result.message ?? 'The player lookup failed.');
        setPlayers(null);
      }
    },
    [client, getToken, segment],
  );

  useEffect(() => {
    // One debounced effect drives every filter: the typed search and state settle after a pause,
    // and a segment change rides the same timer (via `load`'s identity) — a click's extra 250ms is
    // imperceptible, and one code path keeps the filters from racing each other.
    const timer = setTimeout(() => void load(search.trim(), state.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [load, search, state]);

  const segmentOptions = [
    { value: ALL_SEGMENTS, label: 'All segments' },
    ...segments.map((option) => ({ value: option.ref, label: option.label })),
  ];

  return (
    <AdminShell
      title="Players"
      subtitle="Search by the account ref Caesars support supplies."
      actions={
        <div className="flex flex-shrink-0 items-center gap-2">
          <Select
            aria-label="Segment"
            className="w-[150px]"
            options={segmentOptions}
            value={segment}
            onChange={setSegment}
          />
          <Input
            className="w-[110px]"
            aria-label="State"
            placeholder="State"
            value={state}
            onChange={(event) => setState(event.target.value)}
          />
          <SearchInput
            className="w-[280px]"
            pill={false}
            role="searchbox"
            aria-label="Search by account ref"
            placeholder="Account ref"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      }
    >
      {error !== null && (
        <Banner data-testid="players-error" variant="danger" className="mt-4">
          {error}
        </Banner>
      )}

      {players !== null && players.length === 0 && (
        <p data-testid="players-empty" className="mt-8 text-center text-sm text-ink-muted">
          {emptyMessage}
        </p>
      )}

      {players !== null && players.length > 0 && (
        <Table className="mt-4" columns="minmax(0,1.4fr) 140px 90px 130px 130px 90px">
          <THead>
            <TCell>Account ref</TCell>
            <TCell>Segment</TCell>
            <TCell>State</TCell>
            <TCell>First seen</TCell>
            <TCell>Last seen</TCell>
            <TCell>Entries</TCell>
          </THead>
          {players.map((player) => (
            <TRow
              key={player.id}
              data-testid={`player-row-${player.id}`}
              href={`/players/${player.id}`}
            >
              <TCell variant="primary">
                <InfoHint entity="Player" details={playerDetails(player)}>
                  <span className="font-mono text-[13px] text-accent">{player.account}</span>
                </InfoHint>
              </TCell>
              <TCell>
                <SegmentPill>{segmentText(player)}</SegmentPill>
              </TCell>
              <TCell>
                <JurisdictionBadge>{player.state}</JurisdictionBadge>
              </TCell>
              <TCell variant="mono">{formatDateParts(player.createdAt).date}</TCell>
              <TCell variant="mono">{formatDateParts(player.lastSeenAt).date}</TCell>
              <TCell variant="num">{player.entryCount}</TCell>
            </TRow>
          ))}
        </Table>
      )}
    </AdminShell>
  );
}

/**
 * Why the list is empty, in the operator's terms — read from the filters the answer was fetched
 * with. The screen has three controls but the message may only name one the operator actually
 * used: an unfiltered miss means there are no players at all, a search-only miss sends support
 * back to the ref they supplied, and any narrowing — alone or alongside a search — blames the
 * filters, since the ref may well have matched.
 */
function emptyStateMessage(filters: {
  readonly search: string;
  readonly state: string;
  readonly segment: string;
}): string {
  const narrowed = filters.state !== '' || filters.segment !== ALL_SEGMENTS;
  if (narrowed) return 'No players match those filters.';
  return filters.search === '' ? NO_PLAYERS_AT_ALL : 'No player matches that account ref.';
}

/**
 * The segment as an operator should read it: its registry label, or — when the registry has no
 * entry for the ref (a retired segment, or one Caesars has not told us about) — the raw ref, which
 * support can still quote back. Null (the pill renders an em-dash) only when the player carried no
 * `seg` claim at all.
 */
function segmentText(player: AdminPlayer): string | null {
  return player.segmentLabel ?? player.segment ?? null;
}
